import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import api, { profileAPI } from '../services/api';
import { getFriendlyErrorMessage } from '../lib/friendlyErrors';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Check if user is authenticated on mount
  useEffect(() => {
    const checkAuth = async (retryCount = 0) => {
      if (token) {
        try {
          console.log('Checking authentication status...');
          const response = await api.get('/auth/profile');
          console.log('Authentication successful:', response.data.user);
          setUser(response.data.user);
        } catch (error) {
          console.error('Auth check failed:', error);
          if (error.message === 'Request already in progress' && retryCount < 3) {
            console.warn(`Auth request blocked by deduplication (attempt ${retryCount + 1}/3) - retrying in 100ms`);
            // Retry after a short delay for deduplication errors
            setTimeout(() => checkAuth(retryCount + 1), 100);
            return;
          }
          if (error.message === 'Request already in progress') {
            console.warn('Auth request blocked by deduplication - max retries reached, setting loading to false');
            setLoading(false);
            return;
          }
          // Only force logout on true auth failures. For transient backend
          // issues (timeouts/503), keep token and let the user continue.
          if (error.response?.status === 401) {
            logout();
          } else {
            console.warn('Skipping logout due to transient auth/backend error');
          }
        }
      } else {
        console.log('No token found - user not authenticated');
      }
      setLoading(false);
    };

    checkAuth();
  }, [token]);

  // Login function
  const login = async (email, password) => {
    try {
      const response = await api.post('/auth/login', { email, password });
      
      const { token: newToken, user: userData } = response.data;
      
      setToken(newToken);
      setUser(userData);
      localStorage.setItem('token', newToken);
      
      // Set token in API headers
      api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      
      toast.success('Login successful!');
      
      // Role-based routing: Customers go directly to tracker, others to dashboard
      if (userData.role === 'CUSTOMER') {
        navigate('/tracker');
      } else {
        navigate('/dashboard');
      }
      
      return { success: true };
    } catch (error) {
      const message = getFriendlyErrorMessage(
        error,
        'Invalid email or password. Please try again.'
      );
      return { success: false, error: message };
    }
  };

  // Signup function
  const signup = async (userData) => {
    try {
      const response = await api.post('/auth/signup', userData);
      
      // Check if signup was successful
      if (response.data && response.data.success) {
        const { token: newToken, user: newUser } = response.data;
        
        // Normal signup - set token and user
        if (newToken && newUser) {
          setToken(newToken);
          setUser(newUser);
          localStorage.setItem('token', newToken);
          
          // Set token in API headers
          api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
          
          toast.success('Account created successfully!');
          
          // Role-based routing: Customers go directly to tracker, others to dashboard
          if (newUser.role === 'CUSTOMER') {
            navigate('/tracker');
          } else {
            navigate('/dashboard');
          }
        }
        
        return { success: true };
      } else {
        throw new Error(response.data?.error || 'Signup failed');
      }
    } catch (error) {
      const message = error.response?.data?.error || 'Signup failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  // Logout function
  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
    delete api.defaults.headers.common['Authorization'];
    
    toast.success('Logged out successfully');
    navigate('/login');
  };

  // Update user profile
  const updateProfile = async (profileData) => {
    try {
      setLoading(true);
      const response = await api.put('/auth/profile', profileData);
      
      setUser(response.data.user);
      toast.success('Profile updated successfully!');
      
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.error || 'Profile update failed';
      toast.error(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  };

  const setNotificationsEnabled = async (notificationsEnabled) => {
    try {
      const response = await profileAPI.updateNotifications({ notificationsEnabled });
      if (response.data?.success) {
        const enabled = response.data.data?.notificationsEnabled ?? notificationsEnabled;
        setUser((prev) => (prev ? { ...prev, notificationsEnabled: enabled } : prev));
        toast.success(response.data.message || (enabled ? 'Notifications enabled' : 'Notifications disabled'));
        return { success: true };
      }
      throw new Error(response.data?.error || 'Failed to update notification preference');
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Failed to update notification preference';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  // Check if user has required role
  const hasRole = (requiredRoles) => {
    if (!user) return false;
    if (Array.isArray(requiredRoles)) {
      return requiredRoles.includes(user.role);
    }
    return user.role === requiredRoles;
  };

  // Check if user has required permission for an app
  const hasPermission = (app, permission) => {
    if (!user || !user.permissions) return false;
    
    const userPermission = user.permissions[app];
    if (!userPermission) return false;
    
    switch (permission) {
      case 'CRUD':
        return userPermission === 'CRUD';
      case 'RW':
        return ['CRUD', 'RW'].includes(userPermission);
      case 'RO':
        return ['CRUD', 'RW', 'RO'].includes(userPermission);
      default:
        return false;
    }
  };

  // Check if user can access an app
  const canAccessApp = (app) => {
    if (!user || !user.permissions) return false;
    
    return !!user.permissions[app];
  };

  // Get user's accessible apps
  const getAccessibleApps = () => {
    if (!user || !user.permissions) return [];
    
    return Object.entries(user.permissions)
      .filter(([app, permission]) => {
        return permission;
      })
      .map(([app, permission]) => ({
        name: app,
        permission,
        displayName: getAppDisplayName(app)
      }));
  };

  // Get app display name
  const getAppDisplayName = (app) => {
    const appNames = {
      landingPage: 'Landing Page',
      commandControl: 'Command & Control',
      tracker: 'Tracker App',
      configurator: 'Robot Configurator',
      users: 'User Management',
      robots: 'Robot Management',
      fleets: 'Fleet Management'
    };
    return appNames[app] || app;
  };

  // Get app route
  const getAppRoute = (app) => {
    const appRoutes = {
      landingPage: '/landing-page',
      commandControl: '/command-control',
      tracker: '/tracker',
      configurator: '/configurator',
      users: '/users',
      robots: '/robot-manager',
      fleets: '/fleets'
    };
    return appRoutes[app] || '/dashboard';
  };

  const value = {
    user,
    token,
    loading,
    login,
    signup,
    logout,
    updateProfile,
    setNotificationsEnabled,
    hasRole,
    hasPermission,
    canAccessApp,
    getAccessibleApps,
    getAppDisplayName,
    getAppRoute,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
